'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  destination: string;
  programmeLevel: string;
  message: string;
}

export default function ContactPage() {
  useScrollReveal();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    destination: '',
    programmeLevel: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error('Failed to send');

      setSubmitted(true);
    } catch {
      alert('Sorry, there was a problem sending your enquiry. Please try again or email us directly at contact@nexusvantage.eu');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/benjamin-gremler-q_TzfAt4NQ8-unsplash-1779197319592.jpg"
            alt="Barcelona coastline with students enjoying the Mediterranean lifestyle and vibrant city"
            fill
            className="object-cover object-center"
            priority
            quality={90}
          />
          <div className="absolute inset-0 city-overlay" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pb-16 pt-32">
          <p className="eyebrow text-white/80 mb-4">Get in Touch</p>
          <h1 className="font-headline font-extrabold text-white" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Let's Plan Your Journey Together
          </h1>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3 fade-in-up">
              <p className="font-body mb-8" style={{ fontSize: '1.15rem', color: '#3d5566' }}>
                Fill in the form below and one of our consultants will be in touch within 24 hours.
              </p>

              {submitted ? (
                <div
                  className="rounded-2xl p-10 text-center border"
                  style={{ backgroundColor: '#e8f4f9', borderColor: '#2A7FA5' }}
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#2A7FA5' }}>
                    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-headline font-bold mb-3" style={{ fontSize: '1.6rem', color: '#1a2e3b' }}>
                    Thank you for reaching out!
                  </h3>
                  <p className="font-body" style={{ color: '#3d5566', fontSize: '1.1rem' }}>
                    One of our consultants will be in touch within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Full Name <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2"
                      style={{
                        border: '1.5px solid #e0d8c8',
                        color: '#1a2e3b',
                        backgroundColor: '#fdfaf5',
                        focusRingColor: '#2A7FA5',
                      }}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Email Address <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2"
                      style={{ border: '1.5px solid #e0d8c8', color: '#1a2e3b', backgroundColor: '#fdfaf5' }}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Phone Number (with country code) <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2"
                      style={{ border: '1.5px solid #e0d8c8', color: '#1a2e3b', backgroundColor: '#fdfaf5' }}
                    />
                  </div>

                  {/* Country of Origin */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Country of Origin <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <select
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2"
                      style={{ border: '1.5px solid #e0d8c8', color: formData.country ? '#1a2e3b' : '#5a6e7a', backgroundColor: '#fdfaf5' }}
                    >
                      <option value="" disabled>Select your country</option>
                      <option value="India">India</option>
                      <option value="UAE">UAE</option>
                      <option value="Saudi Arabia">Saudi Arabia</option>
                      <option value="Kuwait">Kuwait</option>
                      <option value="Qatar">Qatar</option>
                      <option value="Bahrain">Bahrain</option>
                      <option value="Oman">Oman</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Destination */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Destination of Interest <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <select
                      name="destination"
                      required
                      value={formData.destination}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2"
                      style={{ border: '1.5px solid #e0d8c8', color: formData.destination ? '#1a2e3b' : '#5a6e7a', backgroundColor: '#fdfaf5' }}
                    >
                      <option value="" disabled>Select destination</option>
                      <option value="Spain">Spain</option>
                      <option value="Portugal">Portugal</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>

                  {/* Programme Level */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Programme Level <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <select
                      name="programmeLevel"
                      required
                      value={formData.programmeLevel}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2"
                      style={{ border: '1.5px solid #e0d8c8', color: formData.programmeLevel ? '#1a2e3b' : '#5a6e7a', backgroundColor: '#fdfaf5' }}
                    >
                      <option value="" disabled>Select programme level</option>
                      <option value="Undergraduate">Undergraduate</option>
                      <option value="Masters">Masters</option>
                      <option value="MBA">MBA</option>
                      <option value="LLM">LLM</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block font-body font-semibold text-sm mb-2" style={{ color: '#1a2e3b' }}>
                      Message <span style={{ color: '#2A7FA5' }}>*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us about yourself and what you are looking for..."
                      className="w-full px-4 py-3.5 rounded-xl font-body text-base transition-all duration-200 focus:outline-none focus:ring-2 resize-none"
                      style={{ border: '1.5px solid #e0d8c8', color: '#1a2e3b', backgroundColor: '#fdfaf5' }}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 font-body font-semibold text-white rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#2A7FA5', fontSize: '1.1rem' }}
                  >
                    {submitting ? 'Sending...' : 'Send My Enquiry'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              <div
                className="rounded-2xl p-8 border sticky top-24"
                style={{ backgroundColor: '#F5EDD6', borderColor: '#e0d8c8' }}
              >
                <h3 className="font-headline font-bold mb-8" style={{ fontSize: '1.5rem', color: '#1a2e3b' }}>
                  Contact Details
                </h3>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2A7FA5' }}>
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-sm mb-1" style={{ color: '#1a2e3b' }}>Email</p>
                      <a href="mailto:contact@nexusvantage.eu" className="font-body text-sm transition-colors duration-200 hover:underline" style={{ color: '#2A7FA5' }}>
                        contact@nexusvantage.eu
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2A7FA5' }}>
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-sm mb-1" style={{ color: '#1a2e3b' }}>Website</p>
                      <a href="https://nexusvantage.eu" className="font-body text-sm transition-colors duration-200 hover:underline" style={{ color: '#2A7FA5' }}>
                        nexusvantage.eu
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2A7FA5' }}>
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-sm mb-1" style={{ color: '#1a2e3b' }}>Location</p>
                      <p className="font-body text-sm" style={{ color: '#3d5566' }}>
                        Based in Lisbon, Portugal<br />
                        Serving Students from India &amp; the GCC
                      </p>
                    </div>
                  </li>
                </ul>

                <div className="mt-10 pt-8 border-t" style={{ borderColor: '#d4cbb8' }}>
                  <p className="font-body text-sm font-semibold mb-2" style={{ color: '#1a2e3b' }}>Response Time</p>
                  <p className="font-body text-sm" style={{ color: '#3d5566' }}>
                    We respond to all enquiries within 24 hours, Monday to Friday.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
